// Account Usage Service
// ----------------------
// FE-side accessor for the tenant test-budget API. Calls the BE
// getTenantUsage query, which returns the per-tenant usage projection
// scoped by the caller's Cognito tenantId server-side — the FE never
// passes a tenant identifier.
//
// Scope note: quota is per web app, so per-app pentest cues read
// useTenantEntitlements, NOT this tenant-wide aggregate. This query now
// backs only the tenant-level facts: the SEAT limit (Users page) and the
// tier label (Billing). fetchTenantUsage deliberately does not swallow
// failures — a rejected call propagates so useTenantUsage's error state
// renders instead of showing a misleading fallback.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getTenantUsage } from "@/services/graphql/queries";
import { getGraphData } from "@/utils/transform";
import { CacheKey } from "@/constants/definitions";
import { setCache } from "@/utils/cache";

export interface TenantUsage {
  // Tests consumed this billing period.
  testsUsed: number;
  // Tests allowed this billing period (tier ceiling).
  testsAllowed: number;
  // Period end — when the counter resets. Epoch seconds (BE-aligned).
  periodResetsAt: number;
  // The tier label the BE has on this tenant. Used in copy ("you're
  // on Starter, …") — surface-only string, not a logic gate.
  tierLabel: string;
  // Maximum TOTAL users (incl. the owner) the plan includes. null
  // means UNLIMITED (e.g. enterprise). A number (e.g. 5) is the SME
  // cap. Seat arithmetic lives on the Users page, which is the only
  // surface that knows the current user count.
  seatLimit: number | null;
}

// Force Cognito User Pools auth on the usage query and reject any
// client-side tenantId leak before the request leaves the wrapper —
// symmetric to the BE rule "never accept tenantId from client input."
// Mirrors the wrapper in dataListOperations.service.ts.
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

// Calls getTenantUsage and maps the projection into TenantUsage.
// Lets any AppSync rejection (auth, resolver error, malformed
// response) propagate to the caller — useTenantUsage catches it and
// renders its error state.
export async function fetchTenantUsage(): Promise<TenantUsage> {
  const response = await API.graphql(graphqlOperation(getTenantUsage));
  const usage = getGraphData(response as any, "getTenantUsage");
  if (!usage) {
    // getGraphData returns false on a missing/errored payload. Throw
    // so the hook's catch surfaces an error rather than a silent stub.
    throw new Error("Failed to load tenant usage");
  }
  return {
    testsUsed: usage.testsUsed,
    testsAllowed: usage.testsAllowed,
    periodResetsAt: usage.periodResetsAt,
    tierLabel: usage.tierLabel,
    // seatLimit is nullable in the schema; preserve null (UNLIMITED)
    // and only coerce a defined value.
    seatLimit: usage.seatLimit ?? null,
  };
}

// Warm the tenant-usage cache without rendering anything.
//
// Seat limit and tier are stable, tenant-level facts that only the Client
// Owner can change (and only from one place), so a cached copy cannot go
// meaningfully stale between page views. Fetching during the idle window
// after login turns the Users and Billing pages' visible correction — "1 user
// in your organization" flipping to "1 of 5 users" once the read lands — into
// a cache hit that renders right the first time.
//
// Errors are swallowed by design: speculative warm-up, and useTenantUsage
// still fetches for itself. A failure here costs a slower first paint on those
// two pages, nothing more.
export async function warmTenantUsageCache(): Promise<void> {
  try {
    const usage = await fetchTenantUsage();
    await setCache(CacheKey.getTenantUsage, usage);
  } catch {
    // Non-fatal — see above.
  }
}

// Convenience derivations consumed by every surface that renders
// usage, so each call site doesn't reimplement the same arithmetic.
export interface TenantUsageView extends TenantUsage {
  testsRemaining: number;
  isAtLimit: boolean;
}

export function deriveUsageView(usage: TenantUsage): TenantUsageView {
  const testsRemaining = Math.max(0, usage.testsAllowed - usage.testsUsed);
  return {
    ...usage,
    testsRemaining,
    isAtLimit: testsRemaining === 0,
  };
}
